/**
 * RehearsalNotesIndexPage ✦ — iter205
 * ─────────────────────────────────────────────────────────────
 * Cross-event editorial memory. Operator-only `/debug/rehearsal-
 * notes` (no slug). The brief from iter205:
 *
 *   • "Pattern visibility, not analytics."
 *   • "Editorial memory."
 *   • "The room teaching the roadmap."
 *
 * What this page does:
 *   • Pull every rehearsal note across every event.
 *   • Render them chronologically newest-first, with each note's
 *     originating event_name shown beside the timestamp.
 *   • A tiny per-note pill links back to the per-event pad so
 *     the operator can dive into a single evening's stream when
 *     a pattern catches their eye.
 *
 * What this page does NOT do:
 *   • No counts, no metrics, no engagement scores.
 *   • No search, no filter, no tag taxonomy.
 *   • No export, no analytics dashboard.
 *
 * The operator scans visually. Patterns surface from repetition,
 * not aggregation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INK = '#1f1409';
const INK_SOFT = 'rgba(31,20,9,0.72)';
const INK_FAINT = 'rgba(31,20,9,0.55)';
const PARCHMENT = '#f5efdf';
const GOLD = '#a4824a';
const RULE = 'rgba(31,20,9,0.14)';
const FONT_DISPLAY = "'Cormorant Garamond','Playfair Display',Georgia,serif";
const FONT_BODY = "'Inter',system-ui,sans-serif";


const formatNoteTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit',
    });
    return `${date} · ${time}`;
  } catch {
    return '';
  }
};


const NoteRow = ({ note }) => (
  <article
    data-testid="rehearsal-index-row"
    style={{
      padding: '24px 0',
      borderBottom: `1px solid ${RULE}`,
    }}
  >
    <header style={{
      display: 'flex', alignItems: 'baseline', flexWrap: 'wrap',
      gap: 10, marginBottom: 10,
    }}>
      <span
        data-testid="rehearsal-index-time"
        style={{
          fontFamily: FONT_BODY, fontSize: 11,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: INK_FAINT,
        }}
      >
        {formatNoteTime(note.created_at)}
      </span>
      <Link
        to={`/debug/rehearsal-notes/${note.event_slug}`}
        data-testid="rehearsal-index-event-link"
        style={{
          fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 14, color: GOLD, textDecoration: 'none',
          borderBottom: `1px dotted ${GOLD}`,
        }}
      >
        {note.event_name || note.event_slug}
      </Link>
      {note.context && (
        <span
          data-testid="rehearsal-index-context"
          style={{
            fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 13, color: INK_SOFT,
          }}
        >
          · {note.context}
        </span>
      )}
    </header>
    <p
      data-testid="rehearsal-index-body"
      style={{
        margin: 0, fontFamily: FONT_DISPLAY,
        fontSize: 18, lineHeight: 1.55, color: INK,
        whiteSpace: 'pre-wrap',
      }}
    >
      {note.note}
    </p>
  </article>
);


const RehearsalNotesIndexPage = () => {
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/debug/rehearsal-notes`);
      setNotes(r.data?.notes || []);
    } catch {
      setNotes([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div
      data-testid="rehearsal-notes-index-page"
      style={{
        minHeight: '100vh', background: PARCHMENT, color: INK,
        fontFamily: FONT_BODY, padding: '24px 22px 80px',
      }}
    >
      <BackButton
        fallbackHref="/"
        label="Back"
        variant="on-light"
        testId="rehearsal-notes-index-back-btn"
      />

      <header style={{ maxWidth: 720, margin: '40px auto 0' }}>
        <p
          data-testid="rehearsal-notes-index-eyebrow"
          style={{
            margin: 0, fontFamily: FONT_BODY, fontSize: 11,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: INK_FAINT,
          }}
        >
          REHEARSAL NOTES ✦  ·  ALL EVENINGS  ·  OPERATOR
        </p>
        <h1
          data-testid="rehearsal-notes-index-title"
          style={{
            margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontWeight: 500,
            fontSize: 'clamp(34px,5vw,48px)', lineHeight: 1.05,
            letterSpacing: '-0.015em', color: INK,
          }}
        >
          What the rooms have taught ✦
        </h1>
        <p style={{
          margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 16, color: INK_SOFT, maxWidth: 540, lineHeight: 1.5,
        }}>
          Every observation, every evening, in one quiet stream.
          Patterns surface from repetition.
        </p>
      </header>

      <main
        data-testid="rehearsal-notes-index-stream"
        style={{ maxWidth: 720, margin: '36px auto 0' }}
      >
        {!loaded ? (
          <p style={{
            margin: 0, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 16, color: INK_FAINT, textAlign: 'center',
          }}>
            Finding what the rooms left behind ✦
          </p>
        ) : notes.length === 0 ? (
          <p
            data-testid="rehearsal-notes-index-empty-line"
            style={{
              margin: 0, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 16, color: INK_FAINT, textAlign: 'center',
            }}
          >
            The pads are waiting for the rooms ✦
          </p>
        ) : (
          notes.map((n) => <NoteRow key={n.note_id} note={n} />)
        )}
      </main>
    </div>
  );
};

export default RehearsalNotesIndexPage;
